
import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';

import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AppTypeEnum } from 'src/app/model/appType';
import { Client } from 'src/app/model/client';
import { ExecutionFilter } from 'src/app/model/execution';
import { GenericClass } from 'src/app/model/genericClass';
import { Phase } from 'src/app/model/phase';
import { User } from 'src/app/model/user';
import { ClientService } from 'src/app/services/client.service';
import { ExecutionStatusService } from 'src/app/services/executionStatus.service';
import { PhaseService } from 'src/app/services/phase.service';
import { ProcessService } from 'src/app/services/process.service';
import { UsersService } from 'src/app/services/users.service';
import { Constants } from 'src/app/utils/constants';
import { MyErrorStateMatcher } from 'src/app/utils/errorStateMatcher';
import { CustomDatepickerHeaderComponent } from '../../shared/datepicker-custom-header/datepicker-custom-header.component';


export const MY_FORMATS = {
  // El campo muestra DD/MM/YYYY, asi que eso es lo primero que hay que saber leer.
  // Con solo 'LL' (23 de octubre de 2025) el texto tecleado dependia del analisis
  // permisivo de moment; se declara explicito y se dejan las variantes cortas.
  parse: {
    dateInput: ['DD/MM/YYYY', 'D/M/YYYY', 'LL']
  },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'YYYY'
  }
};

// QLOD-3: sin estos providers el datepicker cae al NativeDateAdapter, que analiza con
// Date.parse y NO entiende "23/10/2025": la fecha escrita a mano llega como null y el
// listado se devuelve entero, sin filtrar y sin avisar. Verificado en PRE: tecleando el
// rango del ticket la peticion sale con workEndFcStart/End a null. El resto de filtros
// de la aplicacion (p.ej. isothermal-characterization-list-filter) si los conserva.
@Component({
    selector: 'app-execution-list-filter',
    templateUrl: './execution-list-filter.component.html',
    providers: [
        {
            provide: DateAdapter,
            useClass: MomentDateAdapter,
            deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS]
        },
        { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
    ],
    standalone: false
})
export class ExecutionListFilterComponent implements OnInit, OnDestroy {

  @Output() filterEmitter = new EventEmitter<ExecutionFilter>();

  filter: ExecutionFilter;

  clients: Client[];
  clientsFiltered: Client[];

  phases: Phase[];
  statuses: GenericClass[];
  processes: GenericClass[];
  users: User[];
  projectManagers: User[];

  expandedFilter = true;

  matcher = new MyErrorStateMatcher();
  form: UntypedFormGroup;

  customDatepickerHeader = CustomDatepickerHeaderComponent;

  private destroy$ = new Subject<void>();

  constructor(
    fb: UntypedFormBuilder,
    private route: ActivatedRoute,
    private clientService: ClientService,
    private phaseService: PhaseService,
    private serviceStatusService: ExecutionStatusService,
    private processService: ProcessService,
    private usersService: UsersService) {
    this.cleanFilter();

    this.form = fb.group({
      idClient: [this.filter.idClient],
      idProcess: [this.filter.idProcess],
      phase: [this.filter.idPhase],
      projectNo: [this.filter.projectNo],
      documentCode: [this.filter.documentCode],
      clientDocumentCode: [this.filter.clientDocumentCode],
      documentCodeProtocol: [this.filter.documentCodeProtocol],
      idStatus: [this.filter.idStatus],

      regUser: [this.filter.regUser],
      projectManager: [this.filter.projectManager],

      regFcStart: [this.filter.regFcStart],
      modFcStart: [this.filter.modFcStart],
      regFcEnd: [this.filter.regFcEnd],
      modFcEnd: [this.filter.modFcEnd],

      workStartFcStart: [this.filter.workStartFcStart],
      workStartFcEnd: [this.filter.workStartFcEnd],
      workEndFcStart: [this.filter.workEndFcStart],
      workEndFcEnd: [this.filter.workEndFcEnd],

      realizationFcStart: [this.filter.realizationFcStart],
      realizationFcEnd: [this.filter.realizationFcEnd],

      equipment: [this.filter.equipment],
      equipmentMaker: [this.filter.equipmentMaker],
      equipmentModel: [this.filter.equipmentModel],
      equipmentSerialNum: [this.filter.equipmentSerialNum],
      equipmentInternalId: [this.filter.equipmentInternalId],
      equipmentLocation: [this.filter.equipmentLocation],

      department: [this.filter.department]
    });
  }

  ngOnInit(): void {
    this.clients = [];
    this.clientService.findAll().pipe(takeUntil(this.destroy$)).subscribe((data: Client[]) => {
      const empty = new Client();
      empty.id = null;
      empty.name = '---';

      data = [empty].concat(data);
      this.clients = data;
      this.clientsFiltered = this.clients.slice();
    }
    );

    this.phases = [];
    this.phaseService.findAll().pipe(takeUntil(this.destroy$)).subscribe((data: Phase[]) => {
      data = data.filter(d => d.enabled);
      this.phases = data;
    });

    this.statuses = [];
    this.serviceStatusService.findAll().pipe(takeUntil(this.destroy$)).subscribe((data: GenericClass[]) => this.statuses = data);

    this.processes = [];
    this.processService.findAll().pipe(takeUntil(this.destroy$)).subscribe((data: GenericClass[]) => this.processes = data);

    this.users = [];
    this.usersService.findAllFromGroupAppType(AppTypeEnum.THERMAL).pipe(takeUntil(this.destroy$))
      .subscribe((data: User[]) => this.users = data);

    this.projectManagers = [];
    this.usersService.findAllRespFromGroup().pipe(takeUntil(this.destroy$)).subscribe((data: User[]) => this.projectManagers = data);

    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe(params => {
      this.filter.idPhase = +params[Constants.FIELD_ID_PHASE];
    });

  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  doFilter(): void {
    this.expandedFilter = false;

    this.filterEmitter.emit(this.filter);
  }

  cleanFilter(): void {
    this.filter = new ExecutionFilter();
  }

  saveDate(field: string, event: MatDatepickerInputEvent<Date> | FocusEvent): void {
    // El mismo metodo atiende a (dateChange) y a (blur). El (blur) entrega un FocusEvent,
    // que no tiene 'value': se leia como undefined, se tomaba como "sin fecha" y se ponia
    // el filtro a null. Y como [ngModel] vuelve a escribir en el control, el campo se
    // quedaba ademas en blanco. Verificado en PRE (QLOD-3): al teclear el rango y salir
    // del campo, la peticion salia con workEndFcStart/End a null y el listado volvia
    // entero, sin filtrar y sin ningun aviso.
    if (event == null || !('value' in event)) {
      return;
    }

    const dateValue = (event as MatDatepickerInputEvent<Date>).value;
    this.filter[field] = dateValue != null ? new Date(dateValue) : null;
  }

}
